import { useEffect, useState } from 'react';
import { Divider, Form, Input, Row } from 'antd';
import { isArray, isEmpty, isNull } from 'lodash';
import InputForm from 'components/InputForm/InputForm';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import SubmitButton from 'components/buttons/SubmitButton/SubmitButton';
import { PLAN_VIEW } from 'modules/auth/constants/authConstants';
import * as EmpAuthService from 'modules/auth/services/EmployeeAuthService';
import { setDependentDetails } from 'modules/auth/slices/employeeAuthSlice';
import {
  hasElections,
  saveElections,
  setOnboardingView,
} from 'modules/auth/slices/idCardSlice';
import LinkButton from 'components/buttons/LinkButton/LinkButton';
import { getText } from 'utils/stringUtil';
import {
  MEMBER_ID,
  THE_MEMBER_ID_CAN_BE_FOUND_AFTER_TEXT,
  SAVE,
  NEXT,
  BACK,
  YOUR_EMPLOYER_DOESN_T_OFFER_PLANS_FOR_THIS_BENEFIT_TEXT,
} from 'constants/languageConstants';
import { WAIVED } from 'modules/auth/constants/globleConstants';

import TickIcon from 'images/shape-icon.svg';
import MedicalIcon from 'images/plan/medical-plan.svg';
import DentalIcon from 'images/plan/dental-plan.svg';
import VisionIcon from 'images/plan/vision-plan.svg';
import { getPublishedPlanIds } from 'utils/commonUtil';

import styles from './mdvPlan.module.less';

type PlanItem = {
  value: string;
  id: string;
  isChecked: boolean;
  isEmpty?: boolean;
};

const MDVPlanSelection = () => {
  const [form] = Form.useForm();
  const { benguide } = useAppSelector((state) => state);
  const { data: benguideData } = benguide || { data: {} };
  const publishedPlans = useAppSelector((state) => state.idCard.publishedPlans);
  const employee = useAppSelector((state) => state.idCard.currentEmployee);
  const inProgress = useAppSelector((state) => state.idCard.inProgress);
  const [selectedPlans, setSelectedPlans] = useState<any>({
    MEDICAL: {},
    DENTAL: {},
    VISION: {},
  });
  const dispatch = useAppDispatch();
  const onBoardingView = useAppSelector((state) => state.idCard.view);
  const election = useAppSelector((state) => state.idCard.electionData) || {};
  const mdvElectionData = useAppSelector(
    (state) => state.idCard.electionMDVIdCardData
  );
  const language = useAppSelector((state) => state.benguide.data?.language);
  const isWaived = selectedPlans[onBoardingView]?.planId === WAIVED.type;

  const filterPublishedPlansBySelectedBenguide = () => {
    const publishedPlanIds = getPublishedPlanIds(benguideData, language)[
      onBoardingView
    ];
    const matchingPlans = publishedPlans?.plans[onBoardingView]?.filter(
      (plan: any) => publishedPlanIds.includes(plan.planId)
    );

    return matchingPlans || [];
  };

  const getCurrentPlans = mdvElectionData?.find(
    (item: any) => item.benefitKind === onBoardingView
  );

  const filteredMDVPlans = filterPublishedPlansBySelectedBenguide();

  const navigateNext = (type: string) => {
    switch (type) {
      case PLAN_VIEW.MEDICAL.type:
        dispatch(setOnboardingView(PLAN_VIEW.DENTAL.type));
        break;
      case PLAN_VIEW.DENTAL.type:
        dispatch(setOnboardingView(PLAN_VIEW.VISION.type));
        break;
      case PLAN_VIEW.VISION.type:
        dispatch(setOnboardingView(PLAN_VIEW.CARD.type));
        break;
      default:
        dispatch(setOnboardingView(PLAN_VIEW.CARD.type));
        break;
    }
  };

  const handleChecked = (type: string, id: string) => {
    const currentPlan = selectedPlans[type];
    if (
      !isEmpty(filteredMDVPlans) &&
      !isEmpty(mdvElectionData) &&
      currentPlan
    ) {
      currentPlan.planId = id;
      currentPlan.memberId =
        id === WAIVED.type ||
        isNull(getCurrentPlans?.coverageDetails?.memberIdNumber)
          ? ''
          : getCurrentPlans?.coverageDetails?.memberIdNumber;
      setSelectedPlans({ ...selectedPlans, [type]: currentPlan });
      form.setFieldsValue({
        memberId: currentPlan.memberId,
      });
    }
  };

  const PlanListItem = ({ value, id, isChecked, isEmpty }: PlanItem) => {
    return (
      <div className={styles.planItemWrapper}>
        <Divider className={styles.itemDivider} />
        <div
          className={`${styles.flexRow} ${isEmpty && styles.removeCursor}`}
          onClick={() => handleChecked(onBoardingView, id)}
        >
          <span
            className={`${styles.listText} ${isChecked && styles.listChecked} ${
              isEmpty && styles.disabledList
            }`}
          >
            {isEmpty
              ? getText(
                  YOUR_EMPLOYER_DOESN_T_OFFER_PLANS_FOR_THIS_BENEFIT_TEXT,
                  language
                )
              : value}
          </span>
          {isChecked && <img src={TickIcon} alt="checked" />}
        </div>
      </div>
    );
  };

  const getIconAndHeader = (type: string) => {
    switch (type) {
      case PLAN_VIEW.MEDICAL.type:
        return (
          <div className={styles.headingRow}>
            <img
              src={MedicalIcon}
              alt={getText(PLAN_VIEW.MEDICAL.value, language)}
            />
            <h1 className={styles.planHeading}>
              {getText(PLAN_VIEW.MEDICAL.value, language)}
            </h1>
          </div>
        );
      case PLAN_VIEW.DENTAL.type:
        return (
          <div className={styles.headingRow}>
            <img
              src={DentalIcon}
              alt={getText(PLAN_VIEW.DENTAL.value, language)}
            />
            <h1 className={styles.planHeading}>
              {getText(PLAN_VIEW.DENTAL.value, language)}
            </h1>
          </div>
        );
      case PLAN_VIEW.VISION.type:
        return (
          <div className={styles.headingRow}>
            <img
              src={VisionIcon}
              alt={getText(PLAN_VIEW.VISION.value, language)}
            />
            <h1 className={styles.planHeading}>
              {getText(PLAN_VIEW.VISION.value, language)}
            </h1>
          </div>
        );
      default:
        return (
          <div className={styles.headingRow}>
            <h1 className={styles.planHeading}>
              {getText(PLAN_VIEW.MEDICAL.value, language)}
            </h1>
            <img src={VisionIcon} alt="" />
          </div>
        );
    }
  };

  const planSelectionHandler = async (values: any) => {
    const currentPlan = selectedPlans[onBoardingView];

    if (currentPlan) {
      currentPlan.memberId =
        currentPlan.planId === WAIVED.type ? '' : values.memberId;
      setSelectedPlans({ ...selectedPlans, [onBoardingView]: currentPlan });
    }
    if (onBoardingView === PLAN_VIEW.VISION.type) {
      const response = await dispatch(
        saveElections(
          employee?.individualId,
          employee?.employerId,
          benguideData?.planYearId,
          selectedPlans
        )
      );
      if (response?.status === 204) {
        const hasElectionResponse = await dispatch(
          hasElections(employee?.individualId, benguideData?.planYearId)
        );

        if (hasElectionResponse?.data?.hasPlans) {
          const dependentsRes = await EmpAuthService.getDependentsDetails(
            employee.employeeId,
            benguideData.planYearId
          );
          dispatch(setDependentDetails(dependentsRes?.data));
        }
      }
    } else {
      navigateNext(onBoardingView);
    }
  };

  const handleCancel = (type: string) => {
    if (!!election?.hasPlans && type === PLAN_VIEW.MEDICAL.type) {
      dispatch(setOnboardingView(PLAN_VIEW.CARD.type));
    } else {
      switch (type) {
        case PLAN_VIEW.MEDICAL.type:
          dispatch(setOnboardingView(PLAN_VIEW.ON_BOARDING.type));
          break;
        case PLAN_VIEW.DENTAL.type:
          dispatch(setOnboardingView(PLAN_VIEW.MEDICAL.type));
          break;
        case PLAN_VIEW.VISION.type:
          dispatch(setOnboardingView(PLAN_VIEW.DENTAL.type));
          break;
        default:
          dispatch(setOnboardingView(PLAN_VIEW.ON_BOARDING.type));
          break;
      }
    }
  };

  const disableNext = () => {
    const plansEmpty = isEmpty(filteredMDVPlans);
    const hasPlanIds = filteredMDVPlans?.some(
      (plan: any) => !isEmpty(plan?.planId)
    );

    if (!plansEmpty && isEmpty(selectedPlans[onBoardingView]?.planId)) {
      return true;
    } else if (
      !plansEmpty &&
      onBoardingView === PLAN_VIEW.VISION.type &&
      !hasPlanIds
    ) {
      return true;
    } else if (inProgress) {
      return true;
    }
    return false;
  };

  const handleInitializePlans = () => {
    if (!isEmpty(mdvElectionData)) {
      const existingMDVData = mdvElectionData?.reduce(
        (acc: any, item: any) => {
          return {
            ...acc,
            [item?.benefitKind]: {
              planId: isWaived ? WAIVED.type : item?.planId,
              memberId: isWaived ? '' : item?.coverageDetails?.memberIdNumber,
            },
          };
        },
        {
          MEDICAL: {},
          DENTAL: {},
          VISION: {},
        }
      );
      setSelectedPlans(existingMDVData);
    } else {
      setSelectedPlans({
        MEDICAL: {},
        DENTAL: {},
        VISION: {},
      });
    }
  };

  useEffect(() => {
    handleInitializePlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEmpty(selectedPlans[onBoardingView]?.planId)) {
      form.setFieldsValue({
        memberId: '',
      });
    } else {
      form.setFieldsValue({
        memberId: isNull(getCurrentPlans?.coverageDetails?.memberIdNumber)
          ? ''
          : getCurrentPlans?.coverageDetails?.memberIdNumber,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlans[onBoardingView], getCurrentPlans]);

  return (
    <InputForm
      isLoading={inProgress}
      form={form}
      layout="vertical"
      onFinish={planSelectionHandler}
    >
      <div className={styles.planContainer}>
        <div className={styles.planListWrapper}>
          {getIconAndHeader(onBoardingView)}
          {isArray(filteredMDVPlans) &&
            [
              ...filteredMDVPlans,
              { name: WAIVED.value, planId: WAIVED.type },
            ].map((item: any) => (
              <PlanListItem
                key={item?.planId}
                value={item?.name}
                id={item?.planId}
                isChecked={
                  selectedPlans[onBoardingView]?.planId === item?.planId
                }
                isEmpty={isEmpty(filteredMDVPlans)}
              />
            ))}
        </div>
        {/* If waived make it not required */}
        {!isEmpty(filteredMDVPlans) && !isWaived && (
          <>
            <Form.Item
              name="memberId"
              label={<>{getText(MEMBER_ID, language)}</>}
            >
              <Input
                disabled={isEmpty(selectedPlans[onBoardingView].planId)}
                className={styles.inputWrapper}
                name="memberId"
              />
            </Form.Item>
            <p className={styles.disabledText}>
              {getText(THE_MEMBER_ID_CAN_BE_FOUND_AFTER_TEXT, language)}
            </p>
          </>
        )}
        <SubmitButton
          type="primary"
          htmlType="submit"
          disabled={disableNext()}
          loading={inProgress}
          className={styles.submitPwdButton}
        >
          {onBoardingView === PLAN_VIEW.VISION.type
            ? getText(SAVE, language)
            : getText(NEXT, language)}
        </SubmitButton>
        <Row justify="center" className={styles.backButton}>
          <LinkButton
            className={styles.resetPwdButton}
            onClick={() => handleCancel(onBoardingView)}
          >
            {getText(BACK, language)}
          </LinkButton>
        </Row>
      </div>
    </InputForm>
  );
};

export default MDVPlanSelection;
